import React from "react";
import APIService from "../../Service/APIService";
import { Button, Input, Tabs, Pagination, Dropdown } from "antd";
import {
  ExclamationCircleFilled,
  CheckCircleFilled,
  FileDoneOutlined,
  FilterOutlined,
  DownOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import FilteredViewChip from "../../common/filtered-view-chip/index.js";
import {
  ORDERS_LINK_KEYS,
  filterChipClearLabel,
  filterChipLabel,
  formatDateRangeLabel,
  readOrdersLinkFilters,
  withoutKeys,
} from "../AABI/landingFilters";
import DateRangeFilter from "./DateRangeFilter";
import Swal from "sweetalert2";
import TitleBar from "../../common/title-bar/title-bar.js";
import CommonButton from "../../common/button/button.js";
import Table from "../../common/table/index.js";
import TableLink from "../../common/table/table-link/index.js";
import StatusIndicator from "../../common/status-indicator/index.js";
import { useScreen } from "../Utils/Screens.js";
import { colors } from "../../theme/colors.js";
import InvoiceFormNew from "../InvoiceForm/InvoiceFormNew.js";
import TickIcon from "../../icons/tick-icon.js";
import TrackIcon from "../../icons/track-icon.js";
import TableOptionsCellMenu from "../../common/table/table-options-cell/table-options-cell-menu.js";
import UpdateTrackingModal from "./UpdateTrackingModal";
import { canUpdateTracking } from "./shippingStatusAccess";
import { message } from "antd";
import AddTrackingModal from "./AddTrackingModal.js";
import TabFade from "../../common/tab-fade";

import { useAuth } from '../AuthProvider.js';
import { hasPermission } from "../../Config/permissions.js";
const { TabPane } = Tabs;

const ORDER_FILTERS = [
  { value: "In Transit", label: "In Transit", directOnly: true },
  { value: "Delayed", label: "Delayed", directOnly: true },
  {
    value: "Delivered",
    label: "Delivered",
    directOnly: true,
    icon: <CheckCircleFilled style={{ color: "#27a907" }} />,
  },
  {
    value: "Approved",
    label: "Approved",
    icon: <CheckCircleFilled style={{ color: "#27a907" }} />,
  },
  {
    value: "Shipped",
    label: "Shipped",
    icon: <CheckCircleFilled style={{ color: colors["primaryColor"] }} />,
  },
  {
    value: "Rejected",
    label: "Rejected",
    icon: <ExclamationCircleFilled style={{ color: "#ff0000" }} />,
  },
  {
    value: "Payment Failed",
    label: "Payment Failed",
    icon: <ExclamationCircleFilled style={{ color: "#ff0000" }} />,
  },
  {
    value: "Completed",
    label: "Completed",
    icon: <FileDoneOutlined style={{ color: colors["primaryColor"] }} />,
  },
  {
    value: "Pending",
    label: "Pending",
  },
];

const ORDER_SORT_FIELDS = new Set([
  "PONumber",
  "ShipToAccountName",
  "OrderDate",
  "DistributorName",
  "OrderTotal",
  "ProductGroup",
  "TotalUOM",
]);

const ORDER_STATUS_VALUES = [...ORDER_FILTERS.map((filter) => filter.value), "Shipping"];

const Orders = () => {
  const { user } = useAuth();
  const userData = user;
  // Filters handed over by the Executive Dashboard (Phase 2 section 8.3).
  // Status and dates were local-only before; they now seed from the URL and
  // write back to it, so a number on the dashboard can deep-link here.
  const location = useLocation();
  const navigate = useNavigate();
  const linkFilters = React.useMemo(
    () => readOrdersLinkFilters(location.search, ORDER_STATUS_VALUES),
    [location.search]
  );
  const linkFiltersKey = JSON.stringify(linkFilters);
  const hasLinkFilters = Boolean(
    linkFilters.uninvoiced ||
      linkFilters.regionId ||
      (linkFilters.startDate && linkFilters.endDate) ||
      (linkFilters.status && linkFilters.from)
  );
  const searchDisabledHint =
    linkFilters.from === "dashboard"
      ? "Clear the Dashboard filter to search"
      : "Clear the imported filter to search";
  const [activeTab, setActiveTab] = React.useState("DirectOrders");
  const [tabData, setTabData] = React.useState({
    DirectOrders: [],
    ConsignmentOrders: [],
    ProductUsage: [],
  });
  const [pagination, setPagination] = React.useState({
    DirectOrders: { page: 1, pageSize: 20, total: 0 },
    ConsignmentOrders: { page: 1, pageSize: 20, total: 0 },
    ProductUsage: { page: 1, pageSize: 20, total: 0 },
  });
  const [sortStateByTab, setSortStateByTab] = React.useState({
    DirectOrders: null,
    ConsignmentOrders: null,
    ProductUsage: null,
  });
  const [showInvoiceModal, setShowInvoiceModal] = React.useState(false);
  const [trackingUpdateOrder, setTrackingUpdateOrder] = React.useState(null);
  const [showAddTrackingModal, setShowAddTrackingModal] = React.useState(false);
  const [orderData, setOrderData] = React.useState(null);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [dateRange, setDateRange] = React.useState(
    linkFilters.startDate && linkFilters.endDate
      ? { startDate: linkFilters.startDate, endDate: linkFilters.endDate }
      : null
  );
  const requestIdRef = React.useRef(0);

  const isPhone = !useScreen("sm");

  const hasTrackingNumber = (value) => String(value ?? "").trim().length > 0;

  const getTableOptionsCellProps = (
    row,
    userData,
    openInvoiceModal,
    openAddTrackingModal
  ) => {
    const {
      OrderType,
      OrderStatus,
      InvoiceID,
      ID,
      IsInvoiceGenerated,
      TrackingNumber,
    } = row;

    const isManager = userData?.Type === "Admin" && hasPermission(userData, "order.manage");
    const hasTracking = hasTrackingNumber(TrackingNumber);
    const isDirectOrder = ["DO", "Direct Order"].includes(OrderType);

    const viewItems = [];
    const shippingItems = [];
    const manageItems = [];
    const dangerItems = [];

    if (OrderStatus === "Completed") {
      viewItems.push({
        label: OrderType === "Consignment Order" ? "View Details" : "View Invoice",
        subVariant: "warning",
        onClick: () => navigate(`/InvoiceHome/${InvoiceID}`),
      });
    }

    if (hasTracking && OrderStatus !== "Completed") {
      shippingItems.push({
        label: "Track Order",
        subVariant: "primary",
        icon: <TrackIcon />,
        onClick: () =>
          window.open(
            `https://www.google.com/search?q=${encodeURIComponent(String(TrackingNumber).trim())}`,
            "_blank",
            "noopener,noreferrer"
          ),
      });
    }

    if (isManager && !hasTracking) {
      shippingItems.push({
        label: "Add Tracking",
        subVariant: "success",
        icon: <TickIcon />,
        onClick: () => openAddTrackingModal?.(row),
      });
    } else if (canUpdateTracking(userData, row)) {
      shippingItems.push({
        label: "Update Tracking",
        subVariant: "primary",
        onClick: () => setTrackingUpdateOrder(row),
      });
    }

    if (
      isManager &&
      (["Approved", "Shipped"].includes(OrderStatus) ||
        (isDirectOrder && ["In Transit", "Delayed", "Delivered"].includes(OrderStatus)))
    ) {
      manageItems.push({
        label: "Add Details",
        subVariant: "primary",
        onClick: () => openInvoiceModal?.(row),
      });
    }

    if (userData?.Type === "Admin" && hasPermission(userData, "order.delete")) {
      dangerItems.push({
        label: "Delete",
        subVariant: "failure",
        onClick: () => {
          Swal.fire({
            title: "Are you sure?",
            text: "Deleting this order will also void the attached Invoice.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: colors["successColor"],
            cancelButtonColor: colors["failureColor"],
            confirmButtonText: "Yes, delete it!",
          }).then(async (result) => {
            if (!result.isConfirmed) return;
            let url = "";
            if (OrderType === "Direct Order" || OrderType === "DO") {
              url = "updatedirectorder?type=delete";
            } else if (OrderType === "Consignment Order" || OrderType === "CO") {
              url = "updateconsignmentorder?type=delete";
            } else if (OrderType === "Product Usage" || OrderType === "PU") {
              url = "updateproductusageorder?type=delete";
            }
            const apiServe = new APIService();
            const response = await apiServe.updateDataToBackend({ ID }, url);
            if (response?.status == 200 || response?.status == 201) {
              Swal.fire({
                icon: "success",
                title: "Order Deleted Successfully",
                showConfirmButton: false,
                timer: 1500,
              });
              window.location.reload();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Unable to delete order",
              });
            }
          });
        },
      });
    }

    return {
      rowData: row,
      actionOption: "Actions",
      actionOptionType: "primary",
      sections: [
        { heading: "View", items: viewItems },
        { heading: "Shipping", items: shippingItems },
        { heading: "Manage", items: manageItems },
        { heading: "Danger", items: dangerItems },
      ],
    };
  };

  const tableHeadersDirectOrders = [
    {
      headerName: "PO Number",
      field: "PONumber",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.PONumber}
          path={`/DirectOrder/${params.data.ID}`}
        />
      ),
    },
    {
      headerName: "Order Date",
      field: "OrderDate",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Product Group",
      field: "ProductGroup",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Total UOM",
      field: "TotalUOM",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Ship To Account",
      field: "ShipToAccountName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.ShipToAccountName}
          path={`/Account/${params.data.AccountID}`}
        />
      ),
    },
    {
      headerName: "Distributor",
      field: "DistributorName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.DistributorName}
          path={`/Distributor/${params.data.DistributorID}`}
        />
      ),
    },
    {
      headerName: "Order Total",
      field: "OrderTotal",
      filter: true,
      sortable: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Status",
      field: "Status",
      filter: true,
      sortable: false,
      cellRenderer: (params) => <StatusIndicator params={params} />,
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      minWidth: 150,
      cellRenderer: (params) => {
        const props = getTableOptionsCellProps(
          params.data,
          userData,
          openInvoiceModal,
          openAddTrackingModal
        );
        return <TableOptionsCellMenu {...props} />;
      },
    },
  ];

  const tableHeadersConsignmentOrders = [
    {
      headerName: "PO Number",
      field: "PONumber",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.PONumber}
          path={`/ConsignmentOrder/${params.data.ID}`}
        />
      ),
    },
    {
      headerName: "Order Date",
      field: "OrderDate",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Product Group",
      field: "ProductGroup",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Total UOM",
      field: "TotalUOM",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Ship To Account",
      field: "ShipToAccountName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.ShipToAccountName}
          path={`/Account/${params.data.AccountID}`}
        />
      ),
    },
    {
      headerName: "Distributor",
      field: "DistributorName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.DistributorName}
          path={`/Distributor/${params.data.DistributorID}`}
        />
      ),
    },
    {
      headerName: "Order Total",
      field: "OrderTotal",
      filter: true,
      sortable: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Status",
      field: "Status",
      filter: true,
      sortable: false,
      cellRenderer: (params) => <StatusIndicator params={params} />,
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      minWidth: 150,
      cellRenderer: (params) => {
        const props = getTableOptionsCellProps(
          params.data,
          userData,
          openInvoiceModal,
          openAddTrackingModal
        );
        return <TableOptionsCellMenu {...props} />;
      },
    },
  ];

  const tableHeadersProductUsage = [
    {
      headerName: "PO Number",
      field: "PONumber",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.PONumber}
          path={`/ProductUsage/${params.data.ID}`}
        />
      ),
    },
    {
      headerName: "Order Date",
      field: "OrderDate",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Product Group",
      field: "ProductGroup",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Total UOM",
      field: "TotalUOM",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Ship To Account",
      field: "ShipToAccountName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.ShipToAccountName}
          path={`/Account/${params.data.AccountID}`}
        />
      ),
    },
    {
      headerName: "Distributor",
      field: "DistributorName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.DistributorName}
          path={`/Distributor/${params.data.DistributorID}`}
        />
      ),
    },
    {
      headerName: "Order Total",
      field: "OrderTotal",
      filter: true,
      sortable: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Status",
      field: "Status",
      filter: true,
      sortable: false,
      cellRenderer: (params) => <StatusIndicator params={params} />,
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      minWidth: 150,
      cellRenderer: (params) => {
        const props = getTableOptionsCellProps(
          params.data,
          userData,
          openInvoiceModal,
          openAddTrackingModal
        );
        return <TableOptionsCellMenu {...props} />;
      },
    },
  ];

  const formatOrders = (orders) => {
    return (orders || []).map((order) => ({
      ...order,
      OrderDate: moment(order.OrderDate).format("MM-DD-YYYY"),
      OrderTotal: `$${parseFloat(order.OrderTotal).toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })}`,
      OrderType:
        order.OrderType === "DO"
          ? "Direct Order"
          : order.OrderType === "CO"
            ? "Consignment Order"
            : order.OrderType === "PU"
              ? "Product Usage"
              : order.OrderType,
      OrderStatus: order.Status,
      Status: (
        <Button
          type={order.Status === "Completed" ? "primary" : "default"}
          shape="round"
          size="small"
          style={{
            color:
              ["Approved", "Delivered"].includes(order.Status)
                ? "#27a907"
                : ["Shipped", "In Transit"].includes(order.Status)
                ? colors["primaryColor"]
                : order.Status === "Completed"
                  ? "#fff"
                  : order.Status === "Payment Failed"
                    ? "red"
                  : order.Status === "Rejected"
                    ? "red"
                    : ["Pending", "Delayed"].includes(order.Status)
                      ? "#ff9202"
                      : "default",
            borderColor:
              ["Approved", "Delivered"].includes(order.Status)
                ? "#27a907"
                : ["Shipped", "In Transit"].includes(order.Status)
                ? colors["primaryColor"]
                : order.Status === "Completed"
                  ? colors["primaryColor"]
                  : order.Status === "Payment Failed"
                    ? "red"
                  : order.Status === "Rejected"
                    ? "red"
                    : ["Pending", "Delayed"].includes(order.Status)
                      ? "#ff9202"
                      : "default",
            background:
              order.Status === "Completed"
                ? colors["primaryColor"]
                : "transparent",
            width: "100%",
          }}
        >
          {order.Status}
        </Button>
      ),
    }));
  };

  const getEndpointForTab = (tabKey) => {
    switch (tabKey) {
      case "DirectOrders":
        return "getdirectorders";
      case "ConsignmentOrders":
        return "getconsignmentorders";
      case "ProductUsage":
        return "getproductusageorders";
      default:
        return "getdirectorders";
    }
  };

  const getOrderTypeForTab = (tabKey) => {
    switch (tabKey) {
      case "DirectOrders":
        return "DirectOrders";
      case "ConsignmentOrders":
        return "ConsignmentOrders";
      case "ProductUsage":
        return "ProductUsage";
      default:
        return "DirectOrders";
    }
  };

  const getStatusParam = () => {
    return selectedFilter ? selectedFilter : "All";
  };

  const appendSortParams = (params, tabKey) => {
    const sortState = sortStateByTab[tabKey];
    if (
      sortState?.field &&
      sortState?.order &&
      ORDER_SORT_FIELDS.has(sortState.field)
    ) {
      params.set("sortField", sortState.field);
      params.set("sortOrder", sortState.order);
    }
  };

  const buildOrdersUrl = (tabKey, page, pageSize) => {
    const params = new URLSearchParams();
    params.set("type", getStatusParam());
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    appendSortParams(params, tabKey);
    if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
    if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
    if (linkFilters.uninvoiced) params.set("uninvoiced", "1");
    if (linkFilters.regionId) params.set("regionId", linkFilters.regionId);
    return `${getEndpointForTab(tabKey)}?${params.toString()}`;
  };

  const buildOrdersSearchUrl = (tabKey, keyword, page, pageSize) => {
    const params = new URLSearchParams();
    params.set("screen", "OrdersDashboard");
    params.set("word", keyword);
    params.set("orderType", getOrderTypeForTab(tabKey));
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    appendSortParams(params, tabKey);
    if (selectedFilter) params.set("status", selectedFilter);
    if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
    if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
    if (linkFilters.uninvoiced) params.set("uninvoiced", "1");
    if (linkFilters.regionId) params.set("regionId", linkFilters.regionId);
    return `search?${params.toString()}`;
  };

  const fetchOrdersForTab = async (tabKey, keywordOverride) => {
    const { page, pageSize } = pagination[tabKey];
    // The keyword search endpoint ignores the imported filters, so it is off
    // while the filter chip is shown.
    const keyword = hasLinkFilters
      ? ""
      : typeof keywordOverride === "string"
        ? keywordOverride
        : searchKeyword;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        keyword
          ? buildOrdersSearchUrl(tabKey, keyword, page, pageSize)
          : buildOrdersUrl(tabKey, page, pageSize)
      );
      if (requestId !== requestIdRef.current) return;
      const rows = response?.data?.data ?? [];
      const formatted = formatOrders(rows);
      setTabData((prev) => ({
        ...prev,
        [tabKey]: formatted,
      }));
      const total = response?.data?.totalCount ?? rows.length;
      if (page > Math.max(1, Math.ceil(total / pageSize))) {
        setPagination(prev => ({ ...prev, [tabKey]: { ...prev[tabKey], total, page: Math.max(1, Math.ceil(total / pageSize)) } }));
        return true;
      }
      setPagination((prev) => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          total,
        },
      }));
      return true;
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      return false;
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const activePage = pagination[activeTab]?.page ?? 1;
  const activePageSize = pagination[activeTab]?.pageSize ?? 20;
  const activeSort = sortStateByTab[activeTab];
  const [selectedFilter, setSelectedFilter] = React.useState(linkFilters.status ?? "");

  React.useEffect(() => {
    fetchOrdersForTab(activeTab);
  }, [
    activeTab,
    activePage,
    activePageSize,
    selectedFilter,
    dateRange,
    searchKeyword,
    activeSort?.field,
    activeSort?.order,
    linkFilters.uninvoiced,
    linkFilters.regionId,
  ]);

  const [isFilter, setIsFilter] = React.useState(Boolean(linkFilters.status));

  // A keyword typed before the filter arrived must not come back when the
  // chip is cleared.
  React.useEffect(() => {
    if (hasLinkFilters) setSearchKeyword("");
  }, [hasLinkFilters]);

  const resetPagination = () => {
    setPagination((prev) => ({
      DirectOrders: { ...prev.DirectOrders, page: 1 },
      ConsignmentOrders: { ...prev.ConsignmentOrders, page: 1 },
      ProductUsage: { ...prev.ProductUsage, page: 1 },
    }));
  };

  const writeSearchParams = (mutate) => {
    const params = new URLSearchParams(location.search);
    mutate(params);
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  // A new link (the URL changed) re-seeds status and dates. The first run is
  // skipped: the initial state already came from the URL.
  const seededLinkKeyRef = React.useRef(linkFiltersKey);
  React.useEffect(() => {
    if (seededLinkKeyRef.current === linkFiltersKey) return;
    seededLinkKeyRef.current = linkFiltersKey;
    setSelectedFilter(linkFilters.status ?? "");
    setIsFilter(Boolean(linkFilters.status));
    setDateRange(
      linkFilters.startDate && linkFilters.endDate
        ? { startDate: linkFilters.startDate, endDate: linkFilters.endDate }
        : null
    );
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkFiltersKey]);

  const clearLinkFilters = () => {
    navigate({
      pathname: location.pathname,
      search: withoutKeys(location.search, ORDERS_LINK_KEYS),
    });
  };

  const handleFilter = (filter) => {
    if (filter === "Clear") {
      setIsFilter(false);
      setSelectedFilter("");
      resetPagination();
      writeSearchParams((params) => params.delete("status"));
      return;
    }
    setIsFilter(true);
    setSelectedFilter(filter);
    resetPagination();
    writeSearchParams((params) => params.set("status", filter));
  };

  const openInvoiceModal = (record) => {
    // console.log("Invoice Modal Opened: ", record);

    setOrderData(record);
    setShowInvoiceModal(true);
  };

  const openAddTrackingModal = (record) => {
    setOrderData(record);
    setShowAddTrackingModal(true);
  };

  const getTabRows = (tabKey) => {
    return tabData[tabKey] || [];
  };

  const handlePaginationChange = (tabKey, page, size) => {
    const current = pagination[tabKey];
    const nextSize = size || current.pageSize;
    const sizeChanged = nextSize !== current.pageSize;
    setPagination((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        page: sizeChanged ? 1 : page,
        pageSize: nextSize,
      },
    }));
  };

  const handleSortChange = (tabKey, nextSort) => {
    const normalizedSort =
      nextSort?.field &&
      nextSort?.order &&
      ORDER_SORT_FIELDS.has(nextSort.field)
        ? nextSort
        : null;
    const currentSort = sortStateByTab[tabKey];
    const isSameSort =
      currentSort?.field === normalizedSort?.field &&
      currentSort?.order === normalizedSort?.order;

    if (isSameSort) return;

    setSortStateByTab((prev) => ({
      ...prev,
      [tabKey]: normalizedSort,
    }));
    setPagination((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        page: 1,
      },
    }));
  };

  const renderStatusFilterButtons = () => {
    const visibleFilters = ORDER_FILTERS.filter(
      (filter) => !filter.directOnly || activeTab === "DirectOrders"
    );
    const selectedOption = visibleFilters.find(
      (filter) => filter.value === selectedFilter
    );
    return (
      <Dropdown
        trigger={["click"]}
        placement="bottomLeft"
        menu={{
          selectedKeys: selectedOption ? [selectedOption.value] : [],
          onClick: ({ key }) => handleFilter(key),
          items: visibleFilters.map((filter) => ({
            key: filter.value,
            label: (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "24px",
                  minWidth: "160px",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {filter.icon ?? <span style={{ width: 14 }} />}
                  {filter.label}
                </span>
                {filter.value === selectedFilter && (
                  <CheckOutlined style={{ color: colors["primaryColor"] }} />
                )}
              </span>
            ),
          })),
        }}
      >
        <Button
          ghost={!selectedOption}
          type={selectedOption ? "primary" : "default"}
          aria-haspopup="menu"
          style={selectedOption ? undefined : { color: "#444", borderColor: "#444" }}
        >
          {selectedOption ? `Status: ${selectedOption.label}` : "Status"}
          <DownOutlined style={{ fontSize: 10 }} />
        </Button>
      </Dropdown>
    );
  };

  const renderTabContent = (tabKey, columnDefs) => {
    const rows = getTabRows(tabKey);
    const pageInfo = pagination[tabKey];
    return (
      <>
        <Table
          rowData={rows}
          columnDefs={columnDefs}
          pagination={false}
          loading={loading}
          serverSideSort
          sortState={sortStateByTab[tabKey]}
          onSortChange={(sortState) => handleSortChange(tabKey, sortState)}
        />
        {!loading && pageInfo.total > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Pagination
              current={pageInfo.page}
              pageSize={pageInfo.pageSize}
              total={pageInfo.total}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={["20", "50", "100"]}
              onChange={(page, size) =>
                handlePaginationChange(tabKey, page, size)
              }
              onShowSizeChange={(page, size) =>
                handlePaginationChange(tabKey, page, size)
              }
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total}`
              }
            />
          </div>
        )}
      </>
    );
  };

  const tabPanes = [
    {
      key: "DirectOrders",
      // title: "Direct Orders",
      // icon: "",
      // content: (
      //   <Table
      //     // loading={loading}
      //     // Search={Input.Search}
      //     dataSource={directOrders}
      //     columns={columns}
      //     components={components}
      //     style={{ marginTop: '-15px' }}
      //     pagination={{
      //       pageSize: pageSize,
      //       total: totalCount,
      //       showSizeChanger: true,
      //       onShowSizeChange: async (cur, ps) => await handleShowSizeChange(cur, ps),
      //       // onChange: async (page, ps) => {
      //       //   console.log('Page Changed: ', page);
      //       //   const curDataCount = page * ps;
      //       //   if (curDataCount > formattedInvoices.length) {
      //       //     await getPaginatedInvoices(page, ps)
      //       //   }
      //       // }
      //     }}
      //   />
      // ),
      content: renderTabContent("DirectOrders", tableHeadersDirectOrders),
      // count: directOrders.length,
    },
    {
      key: "ConsignmentOrders",
      // title: "Consignment Orders",
      // icon: "",
      // content: (
      //   <Table
      //     // loading={loading}
      //     dataSource={consignmentOrders}
      //     columns={columns}
      //     components={components}
      //     style={{ marginTop: '-15px' }}
      //     pagination={{
      //       pageSize: pageSize,
      //       total: totalCount,
      //       showSizeChanger: true,
      //       onShowSizeChange: async (cur, ps) => await handleShowSizeChange(cur, ps),
      //       // onChange: async (page, ps) => {
      //       //   console.log('Page Changed: ', page);
      //       //   const curDataCount = page * ps;
      //       //   if (curDataCount > formattedInvoices.length) {
      //       //     await getPaginatedInvoices(page, ps)
      //       //   }
      //       // }
      //     }}
      //   />
      // ),
      content: renderTabContent(
        "ConsignmentOrders",
        tableHeadersConsignmentOrders
      ),
      // count: consignmentOrders.length,
    },
    {
      key: "ProductUsage",
      // title: "Product Usage",
      // icon: "",
      // content: (
      //   <Table
      //     // loading={loading}
      //     dataSource={productUsage}
      //     columns={columns}
      //     components={components}
      //     style={{ marginTop: '-15px' }}
      //     pagination={{
      //       pageSize: pageSize,
      //       total: totalCount,
      //       showSizeChanger: true,
      //       onShowSizeChange: async (cur, ps) => await handleShowSizeChange(cur, ps),
      //       // onChange: async (page, ps) => {
      //       //   console.log('Page Changed: ', page);
      //       //   const curDataCount = page * ps;
      //       //   if (curDataCount > formattedInvoices.length) {
      //       //     await getPaginatedInvoices(page, ps)
      //       //   }
      //       // }
      //     }}
      //   />
      // ),
      content: renderTabContent("ProductUsage", tableHeadersProductUsage),
      // count: productUsage.length,
    },
  ];

  const changeTab = (key) => {
    setActiveTab(key);
  };
  const handleDateFilterChange = async (dateRange) => {
    setDateRange(dateRange);
    resetPagination();
    writeSearchParams((params) => {
      if (dateRange?.startDate && dateRange?.endDate) {
        params.set("startDate", dateRange.startDate);
        params.set("endDate", dateRange.endDate);
      } else {
        params.delete("startDate");
        params.delete("endDate");
      }
    });
  };

  // The picker shows dates imported from the URL, and re-seeds on a new link.
  const urlDateRange = React.useMemo(
    () =>
      linkFilters.startDate && linkFilters.endDate
        ? [moment(linkFilters.startDate), moment(linkFilters.endDate)]
        : null,
    [linkFilters.startDate, linkFilters.endDate]
  );

  const handleSearch = (value) => {
    const keyword = (value || "").trim();
    setSearchKeyword(keyword);
    resetPagination();
  };

  const renderedInvoiceNumber = pagination[activeTab]?.total ?? 0;

  const closeModal = () => setShowInvoiceModal(false);

  return (
    <div>
      {/* <CustomHeader leftTopText="Welcome back!" leftBottomText="Orders" leftBottomExtraText={`${formattedOrders.length}`}
        rightTopText="Logged in as Admin" rightBottomText="User" /> */}
      <TitleBar
        label="Orders"
        invoiceNumber={renderedInvoiceNumber}
        filterBarComponents={
          <>
            <CommonButton
              variant="timeframe"
              isActive={activeTab === "DirectOrders"}
              onClick={() => changeTab("DirectOrders")}
            >
              Direct Orders
            </CommonButton>
            <CommonButton
              variant="timeframe"
              isActive={activeTab === "ConsignmentOrders"}
              onClick={() => changeTab("ConsignmentOrders")}
            >
              Consignment Orders
            </CommonButton>
            <CommonButton
              variant="timeframe"
              isActive={activeTab === "ProductUsage"}
              onClick={() => changeTab("ProductUsage")}
            >
              Product Usage
            </CommonButton>
          </>
        }
        rightFilterBarComponents={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {hasLinkFilters ? (
              <Input.Search
                key="search-disabled"
                placeholder="Search"
                disabled
                aria-describedby="orders-search-hint"
                style={{ height: "36px" }}
                title={searchDisabledHint}
              />
            ) : (
              <Input.Search
                key="search"
                placeholder="Search"
                // suffix={<SearchOutlined />}
                allowClear
                style={{ height: "36px" }}
                // onSearch={value => setSearchKeyword(value)}
                onChange={(e) => {
                  if (e.target.value === "") {
                    handleSearch("");
                  }
                }}
                onSearch={(value) => handleSearch(value)}
              />
            )}
          </div>
        }
      />
      {hasLinkFilters && (
        <FilteredViewChip
          label={filterChipLabel(linkFilters.from)}
          clearLabel={filterChipClearLabel(linkFilters.from)}
          onClear={clearLinkFilters}
          items={[
            linkFilters.status && linkFilters.from && (linkFilters.status === "Shipping" ? "Shipped, In Transit, Delayed or Delivered" : linkFilters.status),
            linkFilters.uninvoiced && "Approved or shipped, not yet invoiced",
            linkFilters.startDate &&
              linkFilters.endDate &&
              formatDateRangeLabel(linkFilters.startDate, linkFilters.endDate),
            linkFilters.regionId && `Region #${linkFilters.regionId}`,
          ]}
        />
      )}
      {hasLinkFilters && (
        <div
          id="orders-search-hint"
          style={{ margin: "4px 0 0 0", fontSize: "12px", color: colors.lightText }}
        >
          {searchDisabledHint}
        </div>
      )}
      {isPhone ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <div>
            <DateRangeFilter
              key={`${linkFilters.startDate ?? ""}|${linkFilters.endDate ?? ""}`}
              defaultRange={urlDateRange}
              onFilterChange={handleDateFilterChange}
              labelText="Filter by date"
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              overflowX: "scroll",
              gap: "16px",
              scrollbarWidth: "none",
            }}
          >
            {renderStatusFilterButtons()}
            {isFilter && (
              <Button
                ghost
                style={{ color: "#444", borderColor: "#444" }}
                onClick={() => handleFilter("Clear")}
              >
                Clear Filters <FilterOutlined />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            margin: "30px 0px 10px 0px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            {/* <Button icon={<PlusOutlined />} iconPosition='end' ghost style={{ color: '#444', borderColor: '#444' }}>Set Date Range</Button>
          {/* <Button icon={<PlusOutlined />} iconPosition='end' ghost style={{ color: '#444', borderColor: '#444' }}>Add Filter</Button> */}
            <DateRangeFilter
              key={`${linkFilters.startDate ?? ""}|${linkFilters.endDate ?? ""}`}
              defaultRange={urlDateRange}
              onFilterChange={handleDateFilterChange}
              labelText="Filter by date"
            />
            {renderStatusFilterButtons()}
            {isFilter && (
              <Button
                ghost
                style={{ color: "#444", borderColor: "#444" }}
                onClick={() => handleFilter("Clear")}
              >
                Clear Filters <FilterOutlined />
              </Button>
            )}
          </div>
        </div>
      )}
      <Tabs activeKey={activeTab} onChange={changeTab}>
        {tabPanes.map((pane) => (
          <TabPane
            key={pane.key}
          // tab={
          //   <span>
          //     {/* <span>
          //       {pane.icon ? <Icon type={pane.icon || null} /> : null}
          //       {pane.title}
          //     </span> */}
          //     {/* <span
          //       style={{
          //         color: "#B0B0B0",
          //         fontWeight: "300",
          //         marginLeft: "3px",
          //         fontSize: "12px",
          //       }}
          //     >
          //       ({pane.count})
          //     </span> */}
          //   </span>
          // }
          >
            <TabFade activeKey={activeTab}>{pane.content}</TabFade>
          </TabPane>
        ))}
      </Tabs>

      {/* <Table
        // loading={loading}
        dataSource={formattedOrders}
        columns={columns}
        components={components}
        pagination={{
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          onShowSizeChange: async (cur, ps) => await handleShowSizeChange(cur, ps),
          // onChange: async (page, ps) => {
          //   console.log('Page Changed: ', page);
          //   const curDataCount = page * ps;
          //   if (curDataCount > formattedInvoices.length) {
          //     await getPaginatedInvoices(page, ps)
          //   }
          // }
        }}
      /> */}
      {showInvoiceModal && (
        <div>
          <InvoiceFormNew orderType={orderData.OrderType} orderData={orderData} showInvoiceModal={showInvoiceModal} onClose={closeModal} />
        </div>
      )}
      {trackingUpdateOrder && <UpdateTrackingModal key={trackingUpdateOrder.ID} orderData={trackingUpdateOrder} user={userData}
        onClose={() => setTrackingUpdateOrder(null)} onSaved={async result => {
          setTrackingUpdateOrder(null);
          if (result.warning) message.warning(result.warning);
          else message.success(result.notification === "queued" ? "Tracking updated. Email notification queued." : "Tracking updated.");
          const refreshed = await fetchOrdersForTab(activeTab);
          if (!refreshed) message.warning("Tracking updated; unable to refresh orders. Refresh the page to load current orders.");
        }} />}
      {showAddTrackingModal && (
        <AddTrackingModal
          isOpen={showAddTrackingModal}
          onClose={() => setShowAddTrackingModal(false)}
          orderType={orderData.OrderType}
          orderData={orderData}
        />
      )}
    </div>
  );
};

export default Orders;
